import React from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export type InvoiceEditorLineItem = {
  id: string;
  description: string;
  quantity: number;
  unit_amount: number;
};

type InvoiceEditorFormProps = {
  customerFields?: React.ReactNode;
  description: string;
  setDescription: (value: string) => void;
  lineItems: InvoiceEditorLineItem[];
  addLineItem: () => void;
  removeLineItem: (id: string) => void;
  updateLineItem: (id: string, field: keyof InvoiceEditorLineItem, value: string | number) => void;
  discountAmount: number;
  setDiscountAmount: (value: number) => void;
  taxPercentage: number;
  setTaxPercentage: (value: number) => void;
  paymentTerms: string;
  setPaymentTerms: (value: string) => void;
  dueDate: string;
  setDueDate: (value: string) => void;
  memo: string;
  setMemo: (value: string) => void;
  subtotal: number;
  taxAmount: number;
  total: number;
  formatCurrency: (value: number) => string;
  onSubmit: (event: React.FormEvent) => void;
  onCancel: () => void;
  submitLabel: string;
  isSubmitting: boolean;
};

export default function InvoiceEditorForm({
  customerFields,
  description,
  setDescription,
  lineItems,
  addLineItem,
  removeLineItem,
  updateLineItem,
  discountAmount,
  setDiscountAmount,
  taxPercentage,
  setTaxPercentage,
  paymentTerms,
  setPaymentTerms,
  dueDate,
  setDueDate,
  memo,
  setMemo,
  subtotal,
  taxAmount,
  total,
  formatCurrency,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting,
}: InvoiceEditorFormProps) {
  return (
    <form onSubmit={onSubmit}>
      <div className="space-y-6">
        {customerFields}

        <div>
          <label className="mb-1 block text-sm font-medium text-on-surface-variant">
            Invoice Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="e.g., Charter Service - Event Transportation"
            className="input-field"
          />
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="block text-sm font-medium text-on-surface-variant">
              Line Items
            </label>
            <button
              type="button"
              onClick={addLineItem}
              className="inline-flex items-center space-x-1 text-sm text-primary transition-colors hover:text-primary-hover"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Add Line Item</span>
            </button>
          </div>

          <div className="space-y-3">
            {lineItems.map((item) => (
              <div key={item.id} className="admin-subpanel flex items-start space-x-2 rounded-xl p-3">
                <div className="grid flex-1 grid-cols-12 gap-2">
                  <div className="col-span-6">
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(event) => updateLineItem(item.id, 'description', event.target.value)}
                      className="input-field w-full text-sm"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(event) => updateLineItem(item.id, 'quantity', Number.parseInt(event.target.value, 10) || 1)}
                      className="input-field w-full text-sm"
                      required
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Price"
                      value={item.unit_amount}
                      onChange={(event) => updateLineItem(item.id, 'unit_amount', Number.parseFloat(event.target.value) || 0)}
                      className="input-field w-full text-sm"
                      required
                    />
                  </div>
                  <div className="col-span-1 pt-2 text-right text-sm font-medium text-on-surface">
                    {formatCurrency(item.quantity * item.unit_amount)}
                  </div>
                </div>

                {lineItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLineItem(item.id)}
                    className="p-1 text-error transition-colors hover:text-on-error-container"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface-variant">
                Discount Amount ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={discountAmount}
                onChange={(event) => setDiscountAmount(Number.parseFloat(event.target.value) || 0)}
                className="input-field"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-on-surface-variant">
                Tax Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="50"
                step="0.01"
                value={taxPercentage}
                onChange={(event) => setTaxPercentage(Number.parseFloat(event.target.value) || 0)}
                placeholder="e.g., 8.00"
                className="input-field"
              />
              <p className="mt-1 text-xs text-on-surface-variant">Enter percentage values like `8.00` or `8.25`.</p>
            </div>
          </div>

          <div className="admin-subpanel rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-on-surface-variant">Subtotal:</span>
              <span className="font-medium text-on-surface">{formatCurrency(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-success">
                <span>Discount:</span>
                <span>-{formatCurrency(discountAmount)}</span>
              </div>
            )}
            {taxPercentage > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-on-surface-variant">Tax ({taxPercentage.toFixed(2)}%):</span>
                <span className="font-medium text-on-surface">{formatCurrency(taxAmount)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-outline/60 pt-2 text-lg font-bold">
              <span className="text-on-surface">Total:</span>
              <span className="text-primary">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface-variant">Payment Terms</label>
            <select
              value={paymentTerms}
              onChange={(event) => setPaymentTerms(event.target.value)}
              className="input-field"
            >
              <option value="due_on_receipt">Due on Receipt</option>
              <option value="net_15">Net 15 Days</option>
              <option value="net_30">Net 30 Days</option>
              <option value="net_60">Net 60 Days</option>
              <option value="net_90">Net 90 Days</option>
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-on-surface-variant">Due Date (Optional)</label>
            <input
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              className="input-field"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-on-surface-variant">Notes/Memo</label>
          <textarea
            rows={3}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
            placeholder="Internal notes or message to customer..."
            className="input-field"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}